import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { SiteProvider, useSite } from "@/contexts/SiteContext";
import { TrainingProvider } from "@/contexts/TrainingContext";
import { PrintProvider } from "@/contexts/PrintContext";
import { TecorProvider } from "@/contexts/TecorContext";
import { PrintHubProvider } from "@/contexts/PrintHubContext";
import { startSyncEngine, pullSnapshot } from "@/lib/sync";

import PrivateRoute from "@/components/PrivateRoute";
import AppLayout from "@/components/layout/AppLayout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import TemperatureLogs from "@/pages/TemperatureLogs";
import Receival from "@/pages/Receival";
import Cleaning from "@/pages/Cleaning";
import Training from "@/pages/Training";
import PestControl from "@/pages/PestControl";
import HACCP from "@/pages/HACCP";
import Recipes from "@/pages/Recipes";
import Checklists from "@/pages/Checklists";
import LabelPrinter from "@/pages/LabelPrinter";
import Products from "@/pages/Products";
import PrintLog from "@/pages/PrintLog";
import PrintHub from "@/pages/PrintHub";
import LabelVerify from "@/pages/LabelVerify";
import TrustPage from "@/pages/TrustPage";
import SupplierScorecard from "@/pages/SupplierScorecard";
import Settings from "@/pages/Settings";
import QuickScan from "@/pages/QuickScan";
import Help from "@/pages/Help";
import HelpTopic from "@/pages/HelpTopic";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { useParams } from "react-router-dom";

// Short deep-link target printed as a QR on equipment stickers. Scanning
// the fridge/freezer QR with a phone opens the temperature form pre-filled
// for that equipment. Kept short so the printed QR stays small + scannable.
function EquipmentRedirect() {
  const { id } = useParams();
  return <Navigate to={`/temperature?equipment=${encodeURIComponent(id)}`} replace />;
}

function SyncBootstrap() {
  // Listens for active site changes & runs sync engine + initial snapshot pull.
  const { activeSite } = useSite();
  useEffect(() => {
    const stop = startSyncEngine(() => activeSite?.id || null);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (activeSite?.id) pullSnapshot(activeSite.id);
  }, [activeSite]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TrainingProvider>
          <SiteProvider>
          <PrintProvider>
          <TecorProvider>
          <PrintHubProvider>
            <SyncBootstrap />
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* /labels/verify is intentionally OUTSIDE PrivateRoute —
                  the printed-QR deep link must work for any HACCP
                  inspector with a phone camera, no login, no app
                  install. The page calls the public /api/labels/public
                  endpoint when an `id=` query param is present. */}
              <Route path="/labels/verify" element={<LabelVerify />} />
              {/* /trust/:siteId — public marketing dashboard that a
                  restaurant links from their menu or Google profile.
                  Backend gates per-site via Site.public_trust_enabled. */}
              <Route path="/trust/:siteId" element={<TrustPage />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <AppLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="temperature" element={<TemperatureLogs />} />
                <Route path="receival" element={<Receival />} />
                <Route path="cleaning" element={<Cleaning />} />
                <Route path="training" element={<Training />} />
                <Route path="pest" element={<PestControl />} />
                <Route path="haccp" element={<HACCP />} />
                <Route path="recipes" element={<Recipes />} />
                <Route path="checklists" element={<Checklists />} />
                <Route path="labels" element={<LabelPrinter />} />
                <Route path="products" element={<Products />} />
                <Route path="print-log" element={<PrintLog />} />
                <Route path="print-hub" element={<PrintHub />} />
                <Route path="scorecard" element={<SupplierScorecard />} />
                <Route path="settings" element={<Settings />} />
                <Route path="billing" element={<Billing />} />
                <Route path="payment/success" element={<PaymentSuccess />} />
                <Route path="payment/cancel" element={<PaymentCancel />} />
                <Route path="scan" element={<QuickScan />} />
                <Route path="e/:id" element={<EquipmentRedirect />} />
                <Route path="help" element={<Help />} />
                <Route path="help/:slug" element={<HelpTopic />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            <Toaster position="top-right" richColors />
          </PrintHubProvider>
          </TecorProvider>
          </PrintProvider>
          </SiteProvider>
          </TrainingProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
